import React, { useEffect, useState } from 'react';
import { FileListData } from '@typings/ResidentTypes';
import {
  SuccessNotiPositionEnum,
  ShowSuccessNotificationType,
} from '@components/resident/commons';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import { successMessageInMiddleOfSteps } from '@utils/commons';
import DateCompleted from './components/DateCompleted';

import LocationOfMca from './components/LocationOfMca';
import CapacityAssessmentEntry from './components/CapacityAssessmentEntry';
import UploadAssessmentForm from './components/UploadAssessmentForm';
import { hasMCAOptions, MentalCapacityAssessmentType } from './utils';

interface MentalCapacityAssessmentProps {
  userId: string;
  mentalCapacityAssessment: MentalCapacityAssessmentType;
  setMentalCapacityAssessment: React.Dispatch<
    React.SetStateAction<MentalCapacityAssessmentType>
  >;
  uploadedMcaFormListFromData: FileListData[];
  showSuccessNotification: ShowSuccessNotificationType;
  onSaveInMca: (e: any, position: SuccessNotiPositionEnum) => void;
}

const MentalCapacityAssessment: React.FC<MentalCapacityAssessmentProps> = ({
  userId,
  mentalCapacityAssessment,
  setMentalCapacityAssessment,
  uploadedMcaFormListFromData,
  showSuccessNotification,
  onSaveInMca,
}) => {
  const [uploadedMcaFormList, setUploadedMcaFormList] = useState<
    FileListData[]
  >([]);

  useEffect(() => {
    setMentalCapacityAssessment(prev => ({
      ...prev,
      mcaFormList: uploadedMcaFormList,
    }));
  }, [setMentalCapacityAssessment, uploadedMcaFormList]);

  useEffect(() => {
    setUploadedMcaFormList(uploadedMcaFormListFromData);
  }, [uploadedMcaFormListFromData]);

  const handleSave = (position: SuccessNotiPositionEnum) => {
    return (
      <>
        <button
          type="submit"
          className="govuk-button govuk-!-margin-bottom-0"
          data-module="govuk-button"
          disabled={showSuccessNotification.btnLoading}
          onClick={e => onSaveInMca(e, position)}
        >
          Save and continue
        </button>
        {showSuccessNotification.status &&
          showSuccessNotification.position === position && (
            <div className="mt-10">
              <SuccessNotificationBanner
                title={successMessageInMiddleOfSteps.title}
                content={successMessageInMiddleOfSteps.content}
                duration={3000}
                noScrollToTop
              />
            </div>
          )}
      </>
    );
  };

  return (
    <div>
      <div className="govuk-form-group mt-10 govuk-bp:mt-14">
        <fieldset className="govuk-fieldset">
          <legend className="govuk-fieldset__legend govuk-fieldset__legend--l">
            <h1 className="govuk-fieldset__heading">
              Has a mental capacity assessment been carried out
            </h1>
          </legend>
          <div className="govuk-radios" data-module="govuk-radios">
            {hasMCAOptions.map(option => {
              return (
                <div className="govuk-radios__item" key={option.value}>
                  <input
                    className="govuk-radios__input"
                    id="whereDoYouLive"
                    name="whereDoYouLive"
                    type="radio"
                    value={option.value}
                    checked={
                      mentalCapacityAssessment.hasAssessment === option.value
                    }
                    onChange={e =>
                      setMentalCapacityAssessment(prev => ({
                        ...prev,
                        hasAssessment: e.target.value,
                      }))
                    }
                  />
                  <label
                    className="govuk-label govuk-radios__label"
                    htmlFor="whereDoYouLive"
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        </fieldset>
      </div>
      {mentalCapacityAssessment.hasAssessment === 'yes' && (
        <div>
          <DateCompleted
            mentalCapacityAssessment={mentalCapacityAssessment}
            setMentalCapacityAssessment={setMentalCapacityAssessment}
          />
          <CapacityAssessmentEntry
            mentalCapacityAssessment={mentalCapacityAssessment}
            setMentalCapacityAssessment={setMentalCapacityAssessment}
          />
          {handleSave(SuccessNotiPositionEnum.FIRST)}
          <LocationOfMca
            mentalCapacityAssessment={mentalCapacityAssessment}
            setMentalCapacityAssessment={setMentalCapacityAssessment}
          />
          {handleSave(SuccessNotiPositionEnum.SECOND)}
          <UploadAssessmentForm
            userId={userId}
            uploadedMcaFormList={uploadedMcaFormList}
            setUploadedMcaFormList={setUploadedMcaFormList}
          />
        </div>
      )}
    </div>
  );
};

export default MentalCapacityAssessment;
